/**
 * Pre-rendered voice clips (docs/VOICE.md): manifest per locale, clip cache and one shared <audio> player.
 *   - Manifest: GET /api/v1/voice/<locale>/manifest.json (HTTP cache 5 min + ETag); a copy is kept in the Cache API so
 *     cached clips keep working offline.
 *   - Clips: GET /api/v1/voice/<locale>/<file>.m4a (immutable) → Cache API `sketchling-voice-v1`, LRU-capped at 60 MB
 *     (index in localStorage); a small in-memory blob map in front of it.
 *   - Player: a single HTMLAudioElement (plays with the iOS silent switch like Web Speech did, no Range requests thanks
 *     to blob: URLs), paused while the app is hidden, volume = Settings → Sound & feel → Voice volume.
 * tts.ts `speak()` decides when to use it; everything here fails soft (null / 'failed' → Web Speech fallback).
 */
import { SEQ_GAP_MS, resolveSpeech, type SpeechPlan, type VoiceManifest } from '@sketchling/core';
import { isNative } from './platform';
import { useFeelPrefs } from './feelPrefs';
import { isHidden, isLowPower, onPowerChange } from './power';

/** Same origin rule as api.ts (not imported from there: api → store → tts → voice would be a cycle). */
const BASE = `${(import.meta.env.VITE_API_URL as string | undefined) || (isNative ? 'https://api.sketchling.app' : '')}/api/v1/voice`;
const CACHE_NAME = 'sketchling-voice-v1';
const CAP_BYTES = 60 * 1024 * 1024;
const LRU_KEY = 'sk-voice-lru';
const MEM_MAX = 40;

const hasCaches = () => typeof caches !== 'undefined';
const openCache = async (): Promise<Cache | null> => {
  if (!hasCaches()) return null;
  try {
    return await caches.open(CACHE_NAME);
  } catch {
    return null;
  }
};
const urlOf = (locale: string, file: string) => `${BASE}/${locale}/${file}.m4a`;
const manifestUrl = (locale: string) => `${BASE}/${locale}/manifest.json`;

async function fetchT(url: string, ms: number, init?: RequestInit): Promise<Response | null> {
  const ac = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ac ? setTimeout(() => ac.abort(), ms) : null;
  try {
    const r = await fetch(url, { ...init, signal: ac?.signal });
    return r.ok ? r : null;
  } catch {
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/* ------------------------------------------------ manifests ------------------------------------------------ */
const manifests = new Map<string, VoiceManifest | null>();
const loading = new Map<string, Promise<VoiceManifest | null>>();

/** Load (once per session) the manifest of a locale: network, else the offline copy. */
export function loadManifest(locale: string): Promise<VoiceManifest | null> {
  if (manifests.has(locale)) return Promise.resolve(manifests.get(locale)!);
  let p = loading.get(locale);
  if (!p) {
    p = (async () => {
      const url = manifestUrl(locale);
      let m: VoiceManifest | null = null;
      const r = await fetchT(url, 8000);
      if (r) {
        try {
          const body = await r.clone().text();
          m = JSON.parse(body) as VoiceManifest;
          const c = await openCache();
          void c?.put(url, new Response(body, { headers: { 'Content-Type': 'application/json' } })).catch(() => undefined);
        } catch {
          m = null;
        }
      }
      if (!m) {
        try {
          const hit = await (await openCache())?.match(url);
          if (hit) m = (await hit.json()) as VoiceManifest;
        } catch {
          m = null;
        }
      }
      if (m && (m.v !== 1 || typeof m.clips !== 'object')) m = null;
      manifests.set(locale, m);
      loading.delete(locale);
      stats.manifest[locale] = m ? m.version : null;
      return m;
    })();
    loading.set(locale, p);
  }
  return p;
}
export const manifestNow = (locale: string): VoiceManifest | null => manifests.get(locale) ?? null;

/** Plan for a line: waits for the locale manifest at most `waitMs` (first line of a session). */
export async function planFor(locale: string, text: string, waitMs = 900): Promise<SpeechPlan | null> {
  let m = manifests.get(locale);
  if (m === undefined) m = await Promise.race([loadManifest(locale), new Promise<null>((r) => setTimeout(() => r(null), waitMs))]);
  return resolveSpeech(m, text);
}

/* ------------------------------------------------ clip cache ------------------------------------------------ */
type Lru = Record<string, [bytes: number, t: number]>;
function readLru(): Lru {
  try {
    return JSON.parse(localStorage.getItem(LRU_KEY) || '{}') as Lru;
  } catch {
    return {};
  }
}
let lru: Lru | null = null;
let lruTimer: ReturnType<typeof setTimeout> | null = null;
function touch(key: string, bytes?: number) {
  lru ??= readLru();
  const prev = lru[key];
  lru[key] = [bytes ?? prev?.[0] ?? 30000, Date.now()];
  if (!lruTimer)
    lruTimer = setTimeout(() => {
      lruTimer = null;
      void evict();
    }, 1500);
}
async function evict() {
  lru ??= readLru();
  let total = Object.values(lru).reduce((a, [b]) => a + b, 0);
  if (total > CAP_BYTES) {
    const c = await openCache();
    for (const [k, [b]] of Object.entries(lru).sort((a, b) => a[1][1] - b[1][1])) {
      if (total <= CAP_BYTES * 0.9) break;
      const [locale, file] = k.split('/') as [string, string];
      await c?.delete(urlOf(locale, file)).catch(() => false);
      delete lru[k];
      total -= b;
    }
  }
  try {
    localStorage.setItem(LRU_KEY, JSON.stringify(lru));
  } catch {
    /* quota / private mode */
  }
}

const mem = new Map<string, Blob>();
const inflight = new Map<string, Promise<Blob | null>>();
function remember(key: string, b: Blob) {
  mem.delete(key);
  mem.set(key, b);
  while (mem.size > MEM_MAX) mem.delete(mem.keys().next().value!);
}

/** A clip as a Blob: memory → Cache API → network (timeout) → cached. */
export function clipBlob(locale: string, file: string, timeoutMs = 1500): Promise<Blob | null> {
  const key = `${locale}/${file}`;
  const m = mem.get(key);
  if (m) {
    remember(key, m);
    touch(key);
    return Promise.resolve(m);
  }
  let p = inflight.get(key);
  if (!p) {
    p = (async () => {
      const url = urlOf(locale, file);
      const c = await openCache();
      try {
        const hit = await c?.match(url);
        if (hit) {
          const b = await hit.blob();
          remember(key, b);
          touch(key, b.size);
          return b;
        }
      } catch {
        /* fall through to network */
      }
      const r = await fetchT(url, Math.max(timeoutMs, 1500));
      if (!r) return null;
      const b = await r.blob().catch(() => null);
      if (!b || !b.size) return null;
      stats.fetched++;
      remember(key, b);
      touch(key, b.size);
      void c?.put(url, new Response(b, { headers: { 'Content-Type': 'audio/mp4' } })).catch(() => undefined);
      return b;
    })().finally(() => inflight.delete(key));
    inflight.set(key, p);
  }
  return Promise.race([p, new Promise<null>((r) => setTimeout(() => r(null), timeoutMs))]);
}

/**
 * Prefetch the clips of upcoming lines (a whole lesson on open, the next 2 lines while one plays). Battery saver keeps
 * it to the first `lowPowerLimit` lines. Never throws; resolves to the number of clips requested.
 */
export async function prefetchSpeech(locale: string, texts: readonly string[], lowPowerLimit = 3): Promise<number> {
  const m = await loadManifest(locale);
  if (!m) return 0;
  const list = isLowPower() ? texts.slice(0, lowPowerLimit) : texts;
  const files = new Set<string>();
  for (const t of list) for (const c of resolveSpeech(m, t)?.clips ?? []) files.add(c[0]);
  const queue = [...files];
  stats.prefetched += queue.length;
  const worker = async () => {
    for (let f; (f = queue.shift()); ) await clipBlob(locale, f, 15000);
  };
  await Promise.all([worker(), worker(), worker()]);
  return files.size;
}

/* ------------------------------------------------ player ------------------------------------------------ */
let el: HTMLAudioElement | null = null;
let token = 0;
let active = false;
let wake: (() => void) | null = null;
let objectUrl: string | null = null;

function audioEl(): HTMLAudioElement | null {
  if (typeof Audio === 'undefined') return null;
  if (!el) {
    el = new Audio();
    el.preload = 'auto';
    el.setAttribute('playsinline', '');
  }
  return el;
}

/** Play a plan; 'failed' when nothing could start (missing clip / blocked autoplay) so the caller can fall back. */
export async function playPlan(locale: string, plan: SpeechPlan): Promise<'done' | 'stopped' | 'failed'> {
  const my = ++token;
  const a = audioEl();
  if (!a) return 'failed';
  // Start every download now; play in order.
  const blobs = plan.clips.map((c, i) => clipBlob(locale, c[0], i === 0 ? 1500 : 6000));
  let started = false;
  for (let i = 0; i < plan.clips.length; i++) {
    const b = await blobs[i];
    if (my !== token) return 'stopped';
    if (!b) return started ? 'done' : 'failed';
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    objectUrl = URL.createObjectURL(b);
    a.src = objectUrl;
    a.volume = Math.max(0, Math.min(1, useFeelPrefs.getState().voiceVolume ?? 1));
    active = true;
    try {
      await a.play();
    } catch {
      active = false;
      return started || my !== token ? 'stopped' : 'failed';
    }
    started = true;
    stats.played++;
    stats.lastFiles = plan.clips.map((c) => c[0]);
    await new Promise<void>((resolve) => {
      const done = () => {
        a.removeEventListener('ended', done);
        a.removeEventListener('error', done);
        wake = null;
        resolve();
      };
      wake = done;
      a.addEventListener('ended', done);
      a.addEventListener('error', done);
    });
    if (my !== token) return 'stopped';
    if (i < plan.clips.length - 1) await new Promise((r) => setTimeout(r, SEQ_GAP_MS));
  }
  active = false;
  return 'done';
}

export function stopClip(): void {
  token++;
  active = false;
  if (el && !el.paused) el.pause();
  const w = wake;
  wake = null;
  w?.();
}
export const clipActive = () => active;

// Background: pause the clip while hidden, continue when visible again.
onPowerChange(() => {
  if (!el || !active) return;
  if (isHidden()) el.pause();
  else void el.play().catch(() => undefined);
});

/** Counters for the dev panel and e2e (read-only). */
export const stats = {
  manifest: {} as Record<string, string | null>,
  played: 0,
  fallback: 0,
  fetched: 0,
  prefetched: 0,
  lastHow: '' as '' | 'clip' | 'webSpeech' | 'timer',
  lastFiles: [] as string[],
};
